import { isPsHttpError } from '@patsnap/tahiti-core';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { WireFrameExtractDialogAction, type WireFrameExtractDialogResult } from '../types';
import { SaveWireFrameChoiceApi } from '@/domains/specification/apis/wire-frame-extract-api';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { WireFrameExtractSubmissionService } from '../wire-frame-extract-service';
import { WireFrameExtractMessageService } from '../wire-frame-extract-message-service';
import { SpecificationFigureService } from '@/domains/specification/services/specification-figure-service';

@injectable()
export class WireFrameExtractSubmissionServiceImpl implements WireFrameExtractSubmissionService {
    constructor(
        @inject(SaveWireFrameChoiceApi)
        private readonly _saveWireFrameChoiceApi: SaveWireFrameChoiceApi,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(WireFrameExtractMessageService)
        private readonly _messageService: WireFrameExtractMessageService,
        @inject(SpecificationFigureService)
        private readonly _specificationFigureService: SpecificationFigureService,
    ) {}

    private _shouldDiscard(result: WireFrameExtractDialogResult): boolean {
        return result.action === WireFrameExtractDialogAction.DISCARD && !result.wireframeS3Key;
    }

    private async _handleSaveWireFrameChoice(result: WireFrameExtractDialogResult): Promise<void> {
        await this._saveWireFrameChoiceApi.saveWireFrameChoice({
            taskId: this._currentTaskService.taskId,
            original_figure_id: result.originalFigureId,
            wireframe_s3_key: result.wireframeS3Key,
            save_action: this._convertAction(result.action),
        });
        // 保存成功后重新加载附图
        await this._specificationFigureService.reloadFigures();
    }

    private _showSuccessMessage(action: WireFrameExtractDialogAction): void {
        this._messageService.showSuccessMessage(action);
    }

    private _handleError(error: unknown): void {
        if (isPsHttpError(error) && error.cause.error_code === 33140003) {
            this._messageService.showCountOverLimitError();
            return;
        }
        throw error;
    }

    private _convertAction(action: WireFrameExtractDialogAction): 'NEW_FIGURE' | 'REPLACE_ORIGINAL' | 'DISCARD' {
        switch (action) {
        case WireFrameExtractDialogAction.SAVE_AS:
            return 'NEW_FIGURE';
        case WireFrameExtractDialogAction.REPLACE:
            return 'REPLACE_ORIGINAL';
        case WireFrameExtractDialogAction.DISCARD:
            return 'DISCARD';
        default:
            return 'DISCARD';
        }
    }

    async handleSubmit(result: WireFrameExtractDialogResult, next: () => void): Promise<void> {
        if (this._shouldDiscard(result)) {
            next();
            return;
        }

        try {
            await this._handleSaveWireFrameChoice(result);
            this._showSuccessMessage(result.action);
        } catch (error) {
            this._handleError(error);
        } finally {
            next();
        }
    }
}
